import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ApiService } from 'app/shared/api.service';
import { AuthService } from 'app/core/auth/auth.service';
import { isUserRole } from 'app/core/auth/role.constants';

interface DigitalCardData {
  tenant: {
    id: string;
    code: string;
    name: string;
    description?: string;
    status?: string;
    open_at?: string;
    close_at?: string;
  };
  room: {
    id: string;
    label: string;
    status: string;
    doorlock_id?: string;
    grid_no?: number;
    floor?: {
      id?: string;
      label: string;
    };
    type?: {
      id?: string;
      name: string;
      description?: string;
    };
    product?: {
      id?: string;
      name: string;
      periode: string;
      duration?: number;
      duration_label?: string;
      price: number;
      formatted_price: string;
      formatted_period?: string;
    };
    prices: Array<{
      id: string;
      name?: string;
      duration: number;
      unit: string;
      duration_label?: string;
      price: number;
      formatted_price: string;
      formatted_period?: string;
    }>;
    facilities: Array<{
      id: string;
      name: string;
      category: string;
      description?: string;
    }>;
    cards: Array<{
      id: string;
      card_number: string;
      is_active: boolean;
    }>;
  };
  property?: {
    id?: string;
    name?: string;
    address?: string;
    regency?: string;
    province?: string;
    latitude?: number;
    longitude?: number;
  };
  occupant?: {
    id: string;
    name: string;
    user_code?: string;
    phone?: string;
    email?: string;
    address?: string;
    photo_url?: string;
  } | null;
  active_contract?: {
    id: string;
    start_date: string;
    end_date: string;
    grace_date?: string;
    status: string;
    duration: number;
    unit_duration: string;
    total_price: number;
    formatted_total_price: string;
    days_remaining?: number;
    progress_percent?: number;
    payment_status?: string;
  } | null;
  contract_history: Array<{
    id: string;
    occupant_name: string;
    occupant_phone?: string;
    occupant_photo?: string;
    start_date: string;
    end_date: string;
    grace_date?: string;
    duration: number;
    unit_duration: string;
    status: string;
    total_price: number;
    formatted_total_price: string;
    is_current: boolean;
  }>;
  scanned_at?: string;
}

@Component({
  selector: 'app-digital-card',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './digital-card.component.html',
  styleUrls: ['./digital-card.component.scss']
})
export class DigitalCardComponent implements OnInit, OnDestroy {
  tenantCode: string = '';
  code: string = '';
  
  cardData: DigitalCardData | null = null;
  isLoading: boolean = true;
  errorMessage: string = '';
  copySuccess: boolean = false;
  
  activeTab: 'overview' | 'prices' | 'history' | 'location' = 'overview';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private api: ApiService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.tenantCode = params['tenantCode'] || params['username'] || 'bnb';
      this.code = params['code'] || params['username'] || '';

      // If route pattern is bnb/:username where username has the token
      if (params['username'] && !params['code']) {
        this.tenantCode = 'bnb';
        this.code = params['username'];
      }

      if (this.tenantCode && this.code) {
        this.loadCardData();
      } else {
        this.isLoading = false;
        this.errorMessage = 'Parameter kode kartu atau tenant tidak lengkap.';
      }
    });
  }

  ngOnDestroy(): void {}

  async loadCardData(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      const endpoint = `api/v1/card/${this.tenantCode}/${this.code}`;
      const res = await this.api.get(endpoint, false);

      if (res?.success && res?.data) {
        this.cardData = res.data;

        // Cek apakah kamar memiliki kontrak / penghuni aktif
        const hasContract = !!(this.cardData?.occupant || this.cardData?.active_contract);

        if (hasContract) {
          const user = this.authService.getCurrentUser();
          const token = localStorage.getItem('a_id');

          // Jika user SUDAH login sebagai renter -> langsung redirect ke user portal
          if (token && user) {
            const role = this.authService.getUserRole();
            if (role === 'renter' || isUserRole(role)) {
              this.router.navigate(['/user/home'], { replaceUrl: true });
              return;
            }
          }

          // Jika BELUM login -> arahkan ke Sign In biasa yang hanya minta PIN
          const occupantCode = this.cardData?.occupant?.user_code || this.code;
          this.router.navigate(['/sign-in'], { 
            queryParams: { username: occupantCode },
            replaceUrl: true 
          });
          return;
        }

        // Jika TIDAK ADA KONTRAK (Kamar Kosong / Visitor) -> Tampilkan Public Digital Card
        this.isLoading = false;
      } else {
        this.errorMessage = res?.message || 'Data kartu tidak ditemukan atau belum terdaftar.';
        this.isLoading = false;
      }
    } catch (err: any) {
      console.error('[DigitalCard] Error loading card data:', err);
      this.errorMessage = err?.response?.data?.message || err?.message || 'Gagal memuat data kartu digital. Silakan periksa koneksi internet Anda.';
      this.isLoading = false;
    }
  }

  get hasActiveContract(): boolean {
    return !!(this.cardData?.occupant || this.cardData?.active_contract);
  }

  get isOccupied(): boolean {
    return this.cardData?.room?.status?.toLowerCase() === 'occupied' || !!this.cardData?.occupant;
  }

  get roomStatusLabel(): string {
    const s = this.cardData?.room?.status?.toLowerCase();
    switch (s) {
      case 'occupied': return 'Sedang Dihuni';
      case 'available': return 'Tersedia';
      case 'maintenance': return 'Perbaikan';
      case 'booked': return 'Sudah Dibooking';
      default: return 'Tersedia';
    }
  }

  get roomStatusClass(): string {
    const s = this.cardData?.room?.status?.toLowerCase();
    switch (s) {
      case 'occupied': return 'badge-occupied';
      case 'available': return 'badge-available';
      case 'maintenance': return 'badge-maintenance';
      case 'booked': return 'badge-booked';
      default: return 'badge-available';
    }
  }

  get occupantInitials(): string {
    const name = this.cardData?.occupant?.name || '';
    if (!name) return 'P';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }

  get unitDurationLabel(): string {
    const unit = this.cardData?.active_contract?.unit_duration?.toLowerCase();
    switch (unit) {
      case 'daily': return 'Hari';
      case 'weekly': return 'Minggu';
      case 'monthly': return 'Bulan';
      case 'yearly': return 'Tahun';
      default: return 'Bulan';
    }
  }

  formatUnitDuration(unit?: string): string {
    switch (unit?.toLowerCase()) {
      case 'day':
      case 'daily': return 'Hari';
      case 'week':
      case 'weekly': return 'Minggu';
      case 'month':
      case 'monthly': return 'Bulan';
      case 'quarterly': return '3 Bulan';
      case 'semesteral': return '6 Bulan';
      case 'year':
      case 'yearly': return 'Tahun';
      case 'long_term': return 'Bulan';
      default: return 'Bulan';
    }
  }

  formatDate(dateStr?: string | null): string {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('id-ID', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  }

  get facilitiesByCategory(): { [category: string]: any[] } {
    const map: { [category: string]: any[] } = {};
    const facilities = this.cardData?.room?.facilities || [];
    for (const f of facilities) {
      const cat = f.category || 'Fasilitas Lainnya';
      if (!map[cat]) map[cat] = [];
      map[cat].push(f);
    }
    return map;
  }

  get facilityCategoryKeys(): string[] {
    return Object.keys(this.facilitiesByCategory);
  }

  copyShareLink(): void {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      this.copySuccess = true;
      setTimeout(() => this.copySuccess = false, 2500);
    });
  }

  openWhatsApp(phone?: string): void {
    const prop = this.cardData?.property;
    const targetPhone = phone || '6281234567890';
    let cleanPhone = targetPhone.replace(/[^0-9]/g, '');
    if (cleanPhone.startsWith('0')) {
      cleanPhone = '62' + cleanPhone.slice(1);
    }
    const propName = prop?.name || this.cardData?.tenant?.name || 'Kossaya';
    const roomLabel = this.cardData?.room?.label || '';
    const message = encodeURIComponent(`Halo pengelola ${propName}, saya tertarik untuk menanyakan informasi dan survei kamar ${roomLabel}. Apakah unit ini masih tersedia?`);
    window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
  }

  openMaps(): void {
    const prop = this.cardData?.property;
    if (prop?.latitude && prop?.longitude) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${prop.latitude},${prop.longitude}`, '_blank');
    } else if (prop?.address) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(prop.address)}`, '_blank');
    }
  }
}
